import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControlLabel,
  Checkbox,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Chip
} from '@mui/material';
import {
  AccountTree as FrameworkIcon,
  Assignment as TaskIcon,
  Security as ControlIcon,
  Description as DocumentIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckIcon,
  People as PeopleIcon,
  Business as CompanyIcon
} from '@mui/icons-material';

interface WelcomeScreenProps {
  open: boolean;
  onClose: () => void;
  userRole: string;
  userName: string;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ open, onClose, userRole, userName }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('hideWelcomeScreen', 'true');
    }
    onClose();
  };

  const handleSkipTour = () => {
    // Always set the localStorage when Skip Tour is clicked
    localStorage.setItem('hideWelcomeScreen', 'true');
    onClose();
  };

  const steps = [
    {
      label: 'Welcome to ModularCompliance',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Hello {userName}! 👋
          </Typography>
          <Typography variant="body1" paragraph>
            Welcome to ModularCompliance - your comprehensive compliance management platform.
            This quick tour will help you get started.
          </Typography>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Your Role: {userRole}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your role determines what features and modules you can access. 
              We've customized this tour based on your permissions.
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Core Modules Overview',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Key Compliance Modules
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                  <FrameworkIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary="Frameworks" 
                secondary="Define compliance frameworks like GDPR, ISO 27001, or custom regulations"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                  <TaskIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary="Obligations & Requirements" 
                secondary="Break down frameworks into specific obligations and actionable requirements"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                  <ControlIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary="Controls" 
                secondary="Implement controls to address compliance obligations"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                  <DocumentIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary="Tasks & Documents" 
                secondary="Manage compliance tasks and maintain evidence documentation"
              />
            </ListItem>
          </List>
        </Box>
      ),
    },
    {
      label: 'Dashboard & Navigation',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Your Compliance Dashboard
          </Typography>
          <Typography variant="body1" paragraph>
            The dashboard provides a real-time overview of your compliance status:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Overall compliance percentage" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Active frameworks and their status" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Pending tasks and upcoming deadlines" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Recent compliance activities" />
            </ListItem>
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2">
              💡 <strong>Tip:</strong> Use the left navigation menu to access different modules. 
              The menu items shown depend on your role permissions.
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Getting Started',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Your First Steps
          </Typography>
          <Typography variant="body1" paragraph>
            Here's how to get started with compliance management:
          </Typography>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              1. Review Existing Frameworks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Navigate to Frameworks to see compliance frameworks already configured for your organization.
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              2. Check Your Tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visit the Tasks module to see any compliance tasks assigned to you.
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              3. Upload Evidence
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use the Documents module to upload compliance evidence and documentation.
            </Typography>
          </Paper>
          {userRole === 'Administrator' && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
              <Typography variant="subtitle2" gutterBottom>
                4. Manage Users (Admin Only)
              </Typography>
              <Typography variant="body2">
                As an administrator, you can manage users and assign roles in the Users module.
              </Typography>
            </Paper>
          )}
        </Box>
      ),
    },
    {
      label: 'Multi-Company Support',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Working with Multiple Companies
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CompanyIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="body1">
              ModularCompliance supports multi-company management
            </Typography>
          </Box>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Company Switching" 
                secondary="Use the company selector in the top navigation to switch between companies"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Isolation" 
                secondary="Each company's data is completely isolated from others"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Role-Based Access" 
                secondary="Your role may vary between different companies"
              />
            </ListItem>
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2">
              ✅ <strong>Security:</strong> You can only see data for companies where you have access.
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Need Help?',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Support & Resources
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><PeopleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Contact Support" 
                secondary="Email: support@modularcompliance.com"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><DocumentIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Documentation" 
                secondary="Access detailed guides for each module"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><DashboardIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Video Tutorials" 
                secondary="Watch step-by-step tutorials (coming soon)"
              />
            </ListItem>
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Ready to get started? 🚀
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Remember, you can always access this guide from the Help menu.
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h5" component="div">
          Welcome to ModularCompliance
        </Typography>
        <Chip 
          label="Alpha UAT" 
          size="small" 
          sx={{ 
            bgcolor: 'warning.main',
            color: 'warning.contrastText'
          }} 
        />
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                {step.content}
                <Box sx={{ mb: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                    disabled={index === steps.length - 1}
                  >
                    {index === steps.length - 1 ? 'Finish' : 'Continue'}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        {activeStep === steps.length - 1 && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={dontShowAgain} 
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
              }
              label="Don't show this welcome guide again"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSkipTour} variant="text">
          Skip Tour
        </Button>
        {activeStep === steps.length - 1 && (
          <Button onClick={handleClose} variant="contained" color="primary">
            Get Started
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeScreen;
